import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, Button, Card, CardBody, Col, Container, Row, Table } from "reactstrap";
import { useAppDispatch } from "src/store";
import {
  brevoAddDomain,
  brevoVerifyDomain,
  getMailProvisioning,
} from "src/store/mail-provisioning/saga";
import { MailProvisioningRow } from "src/store/mail-provisioning/type";

/**
 * Admin "E-posta Sağlama" ekranı — tüm custom-domain tenant'ların Web/Mailcow
 * durumu. Giden mail 2026-09-08'den beri kendi sunucumuzdan çıkıyor (Hetzner 25/465'i
 * açtı); ayrı bir sağlayıcı doğrulaması yok. "Ekle" domaini posta sunucusuna kurar, DNS kayıtları tenant
 * paneline düşer, tenant ekleyip doğrular.
 */
const MailProvisioningPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<MailProvisioningRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>("");

  document.title = t("MailProvisioning.PageTitle", "E-posta Sağlama");

  const load = () => {
    setLoading(true);
    dispatch(
      getMailProvisioning({
        payload: {
          onSuccess: (_m: any, res: MailProvisioningRow[]) => {
            setRows(Array.isArray(res) ? res : []);
            setLoading(false);
          },
          onError: () => setLoading(false),
        },
      })
    );
  };
  useEffect(() => {
    load();
  }, []);

  const isAuth = (r: MailProvisioningRow) =>
    r.brevoAuthenticated || r.brevoStatus === "authenticated";

  const doAdd = (r: MailProvisioningRow) => {
    setBusy(r.clientId);
    dispatch(
      brevoAddDomain({
        body: { ClientId: r.clientId },
        payload: {
          onSuccess: () => {
            setBusy("");
            toast.success(
              t("MailProvisioning.Added", "Alan adı posta sunucusuna eklendi.")
            );
            load();
          },
          onError: (m: string) => {
            setBusy("");
            toast.error(m || t("MailProvisioning.Error", "İşlem başarısız."));
          },
        },
      })
    );
  };

  const doVerify = (r: MailProvisioningRow) => {
    setBusy(r.clientId);
    dispatch(
      brevoVerifyDomain({
        body: { ClientId: r.clientId },
        payload: {
          onSuccess: (_m: any, res: any) => {
            setBusy("");
            if (res?.authenticated)
              toast.success(t("MailProvisioning.Authenticated", "Doğrulandı — giden e-posta aktif."));
            else
              toast.info(
                t("MailProvisioning.NotYet", "Henüz doğrulanmadı; DNS yayılımını bekleyip tekrar deneyin.")
              );
            load();
          },
          onError: (m: string) => {
            setBusy("");
            toast.error(m || t("MailProvisioning.Error", "İşlem başarısız."));
          },
        },
      })
    );
  };

  const statusBadge = (r: MailProvisioningRow) => {
    if (isAuth(r)) return <Badge color="success">{t("MailProvisioning.StatusAuthenticated", "Doğrulandı")}</Badge>;
    if (r.brevoStatus === "added" || r.brevoInAccount)
      return <Badge color="info">{t("MailProvisioning.StatusAdded", "Eklendi (doğrulama bekliyor)")}</Badge>;
    return <Badge color="warning">{t("MailProvisioning.StatusPending", "Onay bekliyor")}</Badge>;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="mb-3 align-items-center">
            <Col>
              <h4 className="mb-0">{t("MailProvisioning.Title", "E-posta Sağlama")}</h4>
              <p className="text-muted mb-0">
                {t(
                  "MailProvisioning.Subtitle",
                  "Müşteri alan adlarının giden e-posta durumunu yönetin. Mail kendi sunucumuzdan gönderiliyor; MX ve DKIM tamamlandığında gönderim açılır."
                )}
              </p>
            </Col>
            <Col xs="auto">
              <Button color="light" onClick={load}>
                <i className="bx bx-refresh me-1" />
                {t("MailProvisioning.Refresh", "Yenile")}
              </Button>
            </Col>
          </Row>

          <Card>
            <CardBody>
              <div className="table-responsive">
                <Table className="table-nowrap align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>{t("MailProvisioning.Tenant", "Müşteri")}</th>
                      <th>{t("MailProvisioning.Domain", "Alan adı")}</th>
                      <th>{t("MailProvisioning.Mailcow", "Mailcow")}</th>
                      <th>{t("MailProvisioning.Outbound", "Giden e-posta")}</th>
                      <th className="text-end">{t("MailProvisioning.Actions", "İşlemler")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          {t("MailProvisioning.Loading", "Yükleniyor…")}
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-4 text-muted">
                          {t("MailProvisioning.Empty", "Özel alan adı olan müşteri yok.")}
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.clientId}>
                          <td>{r.clientName || "—"}</td>
                          <td>
                            <code>{r.domain}</code>
                          </td>
                          <td>
                            {r.inMailcow ? (
                              <Badge color="success">✓</Badge>
                            ) : (
                              <Badge color="secondary">—</Badge>
                            )}
                          </td>
                          <td>{statusBadge(r)}</td>
                          <td className="text-end">
                            {isAuth(r) ? (
                              <span className="text-success fw-bold">✓</span>
                            ) : r.brevoStatus === "pending_admin" && !r.brevoInAccount ? (
                              <Button
                                size="sm"
                                color="primary"
                                disabled={busy === r.clientId}
                                onClick={() => doAdd(r)}
                              >
                                {t("MailProvisioning.AddBrevo", "Brevo'ya Ekle")}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                color="secondary"
                                outline
                                disabled={busy === r.clientId}
                                onClick={() => doVerify(r)}
                              >
                                {t("MailProvisioning.Verify", "Doğrula")}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MailProvisioningPage;
